<script module lang="ts">
	import type { DotColor, MarkerScheme } from '$lib/components/map/markers/markerContent/types';

	const colorClasses: Record<MarkerScheme, string> = {
		hotel:
			'text-neutral-weakest bg-radial-gradient-hotel before:bg-hotel border-hotel-strong after:bg-hotel-strong',
		hotelMichelin:
			'text-neutral-weakest bg-radial-gradient-hotel-michelin before:bg-hotel-michelin border-hotel-michelin-strong after:bg-hotel-michelin-strong',
		restaurant:
			'text-neutral-weakest bg-radial-gradient-restaurant before:bg-restaurant border-restaurant-strong after:bg-restaurant-strong',
		restaurantMichelin:
			'text-neutral-weakest bg-radial-gradient-restaurant-michelin before:bg-restaurant-michelin border-restaurant-michelin-strong after:bg-restaurant-michelin-strong',
		tourism:
			'text-neutral-weakest bg-radial-gradient-tourism before:bg-tourism border-tourism-strong after:bg-tourism-strong',
		parking:
			'text-neutral-weakest bg-radial-gradient-parking before:bg-parking border-parking-strong after:bg-parking-strong',
		energy:
			'text-neutral-weakest bg-radial-gradient-fuel before:bg-fuel border-fuel-strong after:bg-fuel-strong',
		electric:
			'text-neutral-weakest bg-radial-gradient-electric before:bg-electric border-electric-strong after:bg-electric-strong',
		rest: 'text-neutral-weakest bg-radial-gradient-rest-stop-soft before:bg-rest-stop-soft border-rest-stop after:bg-rest-stop',
		restSponsored:
			'text-neutral-weakest bg-radial-gradient-rest-stop-strong before:bg-rest-stop-strong border-rest-stop-strong after:bg-rest-stop-strong',
		carAccident:
			'text-neutral-weakest bg-radial-gradient-error-soft before:bg-error-soft border-error after:bg-error',
		carIncident:
			'text-neutral-weakest bg-radial-gradient-error-soft before:bg-error-soft border-error after:bg-error',
		carDangerous:
			'text-neutral-weakest bg-radial-gradient-warning-soft before:bg-warning-soft border-warning after:bg-warning',
		laneClosed:
			'text-neutral-weakest bg-radial-gradient-warning-soft before:bg-warning-soft border-warning after:bg-warning',
		constructionCone:
			'text-neutral-weakest bg-radial-gradient-coloredneutral-soft before:bg-coloredneutral-soft border-coloredneutral after:bg-coloredneutral',
		weatherHail:
			'text-neutral-weakest bg-radial-gradient-coloredneutral-soft before:bg-coloredneutral-soft border-coloredneutral after:bg-coloredneutral',
	};

	const dotColorsMap: Record<DotColor, string> = {
		green: 'bg-success',
		orange: 'bg-warning',
		red: 'bg-error',
	};
</script>

<script lang="ts">
	import type { MarkerStyle } from '$lib/components/map/markers/markerContent/types';
	import type { Snippet } from 'svelte';
	import type { ClassValue } from 'svelte/elements';
	import Image from '$lib/components/atoms/image/Image.svelte';
	import Icon from '$lib/components/icons/Icon.svelte';
	import { getStyle } from '$lib/utils/getStyle';

	interface Props {
		scheme?: MarkerScheme;
		recommended?: boolean;
		isHovered?: boolean;
		dot?: DotColor;
		imgUrl?: string;
		imgSize?: 'sm' | 'md';
		colorStyles?: MarkerStyle;
		tag?: string;
		iconTag?: string;
		class?: ClassValue;
		children?: Snippet;
		sponsor?: 'kleber';
		ariaRole?: 'link' | 'button';
		onmouseenter?: (event: MouseEvent) => void;
		onmouseleave?: (event: MouseEvent) => void;
	}

	const {
		scheme,
		recommended = false,
		isHovered = false,
		dot,
		imgUrl,
		imgSize = 'md',
		colorStyles,
		tag = 'div',
		iconTag = 'div',
		sponsor,
		class: classes,
		children,
		ariaRole,
		onmouseenter,
		onmouseleave,
	}: Props = $props();

	const dotColorClass = $derived(dot ? dotColorsMap[dot] : '');
	const style = $derived(imgUrl && colorStyles ? getStyle<MarkerStyle>(colorStyles) : '');

	const markerStyle = $derived.by(() => {
		let styleValue = style;
		if (colorStyles) {
			styleValue += `--arrow-background-color: ${colorStyles['background-color']}; --arrow-border-color: ${colorStyles['border-color']};`;
		}
		return styleValue;
	});
</script>

<svelte:element
	this={tag}
	class={[
		'poi-marker relative px-0.5 flex items-center justify-center text-4 shadow-smooth-bottom-1',
		isHovered && 'poi-marker-highlight',
		scheme && colorClasses[scheme],
		style && 'poi-marker-with-style',
		classes,
	]}
	data-testid="map-marker-{scheme}"
	style={markerStyle}
	role={ariaRole}
	{onmouseenter}
	{onmouseleave}
>
	{#if dot}
		<span
			class={[
				'size-2.5 absolute top--1.5 right--1.5 rounded-full shadow-smooth-bottom-1',
				dotColorClass,
			]}
		></span>
	{:else if recommended}
		<Icon
			class="absolute top--2 right--2 rounded-full shadow-smooth-bottom-1"
			icon="recommended"
			ariaLabel=""
			tag={iconTag}
		/>
	{:else if sponsor === 'kleber'}
		<div
			class="absolute top--2 right--2 rounded-full size-4 bg-white flex items-center justify-center shadow-smooth-bottom-1"
		>
			<svg class="w-3.5 h-auto" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
				<path
					d="M4.97723 10.0183C5.20061 10.0825 5.35291 10.1154 5.50521 10.1154C5.65751 10.1154 5.80135 10.0806 5.99257 10.0089L12.8461 7.50447L11.0211 6.98335C10.8045 6.92225 10.6471 6.88464 10.4948 6.88464C10.3425 6.88464 10.1986 6.92225 10.0074 6.99119L3.15384 9.49809L4.97723 10.0183Z"
					fill="#E30521"
				/>
				<path
					d="M10.2587 10.0228C10.4725 10.0816 10.6262 10.1154 10.7753 10.1154C10.9245 10.1154 11.0686 10.0831 11.2526 10.0169L14.8156 8.77552C14.935 8.73437 15 8.66823 15 8.60503C15 8.53448 14.935 8.47274 14.7957 8.43453L13.0867 7.96155L8.53847 9.5507L10.2587 10.021"
					fill="#E30521"
				/>
				<path
					d="M1.16551 8.56502L2.59158 9.03849L6.38462 7.45045L4.95026 6.97698C4.77196 6.91817 4.64376 6.88464 4.51902 6.88464C4.39428 6.88464 4.27714 6.91846 4.1213 6.98287L1.14962 8.228C1.0501 8.26918 1 8.33387 1 8.39681C1 8.46768 1.0501 8.52944 1.16621 8.5659"
					fill="#E30521"
				/>
			</svg>
		</div>
	{:else if imgUrl}
		<Image src={imgUrl} class={imgSize === 'sm' ? 'size-3.75' : 'size-5'} alt="" />
	{/if}
	{@render children?.()}
</svelte:element>

<style>
	.poi-marker {
		border-width: 1px;
		border-style: solid;
		border-radius: 3px;
		height: 26px;
		min-width: 26px;
		width: fit-content;
		position: relative;
		&::before {
			content: "";
			position: absolute;
			width: 8.6px;
			height: 4.7px;
			left: calc(50% - 4.3px);
			bottom: -4.7px;
			clip-path: path(
				"M9.07 0V0C9.06807 0.552943 8.59271 0.979965 8.10748 1.24511C7.82785 1.39791 7.58456 1.61755 7.40242 1.89077L5.8282 4.2521C5.43238 4.84583 4.55992 4.84583 4.1641 4.2521L2.58988 1.89077C2.40772 1.61753 2.16442 1.39788 1.88476 1.24508C1.40064 0.980565 0.927673 0.551729 0.93 7.05719e-05V7.05719e-05L9.07 0Z"
			);
		}
		&::after {
			content: "";
			position: absolute;
			width: 8.6px;
			height: 4.7px;
			left: calc(50% - 4.3px);
			bottom: -4.7px;
			clip-path: path(
				"M0.929688 0C1.93275 0 2.86944 0.501303 3.42584 1.3359L5.00006 3.69722L6.57427 1.3359C7.13067 0.501303 8.06736 0 9.07042 0V1C8.40172 1 7.77725 1.3342 7.40632 1.8906L5.83211 4.25193C5.43628 4.84566 4.56383 4.84566 4.168 4.25193L2.59379 1.8906C2.22286 1.3342 1.59839 1 0.929688 1V0Z"
			);
		}
		&::before,
		&::after {
			transition-timing-function: ease-in;
			transition-duration: 0.15s;
		}
	}

	.poi-marker-with-style {
		&::before {
		background-color: var(--arrow-background-color);
	}

		&::after {
			background-color: var(--arrow-border-color);
		}
	}

	@keyframes translate {
		0% {
			transform: translateY(0) scale(1.2);
			animation-duration: 0.3s;
			animation-timing-function: ease-out;
		}

		60% {
			transform: translateY(-8px) scale(1.2);
			transition-timing-function: ease-out;
			transition-duration: 0.4s;
		}

		100% {
			transform: scale(1.2);
		}
	}
	.poi-marker-highlight {
		animation-iteration-count: infinite;
		animation-name: translate;
		animation-duration: 0.7s;
	}

	.poi-marker {
		transform: translateY(0);
		transition-duration: 0.15s;
		transition-timing-function: ease-in;
	}

	.poi-marker:hover,
	.poi-marker.poi-marker-without-animation {
		animation: none;
		transform: translateY(-1px) scale(1.2);
		transition: all 0.15s ease-out;
		--uno: shadow-material-elevation-3;
	}

	.poi-marker:hover::before,
	.poi-marker.hovered::before,
	.poi-marker:hover::after,
	.poi-marker.hovered::after {
		transition: all 0.15s ease-out;
		background-color: var(--color-coloredneutral-strongest);
	}
</style>
